import { Code, ConnectError, type Interceptor } from '@connectrpc/connect';
import { createConnectTransport } from '@connectrpc/connect-web';
import { isGenuineSessionExpiryError, reportSessionExpired, requestFreshToken } from './authBridge';

// Strip the "[code] " prefix that ConnectError bakes into Error.message so that
// UI components can use error.message directly for display without needing to
// know about Connect's internal formatting. The code is still available via
// error.code for programmatic checks.
const stripCodePrefixInterceptor: Interceptor = (next) => async (req) => {
  try {
    return await next(req);
  } catch (err) {
    if (err instanceof ConnectError) {
      err.message = err.rawMessage;
    }
    throw err;
  }
};

/**
 * Create a Connect RPC transport for API calls
 * Uses backend URL directly - backend handles CORS
 *
 * @param baseUrl - Optional base URL override (defaults to VITE_API_BASE_URL or http://localhost:8080)
 */
export function createApiTransport(baseUrl?: string) {
  return createConnectTransport({
    baseUrl: baseUrl || import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080',
    interceptors: [stripCodePrefixInterceptor],
  });
}

/**
 * Interceptor for authenticated transports: attaches the bearer token, and
 * on Unauthenticated (401), asks the auth bridge for a fresh token and
 * retries once (unary requests only — a streaming request's message may
 * already be a partially-consumed AsyncIterable, unsafe to replay).
 * Exported for direct unit testing.
 */
export function createAuthRefreshInterceptor(accessToken: string): Interceptor {
  return (next) => async (req) => {
    req.header.set('Authorization', `Bearer ${accessToken}`);

    try {
      return await next(req);
    } catch (err) {
      if (!(err instanceof ConnectError) || err.code !== Code.Unauthenticated) {
        throw err;
      }

      let freshToken: string;
      try {
        freshToken = await requestFreshToken();
      } catch (refreshErr) {
        if (isGenuineSessionExpiryError(refreshErr)) reportSessionExpired();
        throw err; // surface the original Unauthenticated error
      }

      if (req.stream) {
        // The request body may already be a partially/fully-consumed
        // AsyncIterable — unsafe to replay. The fresh token was already
        // pushed to subscribers via requestFreshToken() above, so the
        // *next* call succeeds without needing a retry here.
        throw err;
      }

      req.header.set('Authorization', `Bearer ${freshToken}`);
      try {
        return await next(req);
      } catch (retryErr) {
        if (retryErr instanceof ConnectError && retryErr.code === Code.Unauthenticated) {
          reportSessionExpired();
        }
        throw retryErr;
      }
    }
  };
}

/**
 * Create an authenticated Connect RPC transport that includes a Bearer token
 *
 * @param accessToken - JWT access token to include in Authorization header
 * @param baseUrl - Optional base URL override
 */
export function createAuthApiTransport(accessToken: string, baseUrl?: string) {
  return createConnectTransport({
    baseUrl: baseUrl || import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080',
    interceptors: [createAuthRefreshInterceptor(accessToken), stripCodePrefixInterceptor],
  });
}
